<template>
  <div class="tour-participants">
    <transition name="fade">
      <div v-if="participants.length" class="tour-participants__container">
        <div class="tour-participants__list">
          <tooltip
            v-for="({
              id,
              user_link,
              first_name,
              avatar_resize,
              city,
            }, index) in croppedParticipants"
            :key="id"
            class="tour-participants__avatar"
            :class="{hidden: index >= 4 }"
          >
            <div
              :title="first_name"
              class="tour-participants__avatar-link"
              @click="showDetailPopup(id)"
            >
              <avatar :name="first_name" :photo="avatar_resize" />
            </div>

            <template #content>
              <div class="u-text-center">
                <div>{{ first_name }}</div>
                <div v-if="city" class="u-text-secondary">{{ city }}</div>
              </div>
            </template>
          </tooltip>

          <tooltip
            v-if="participants.length > 4">
            <div
              @click="popups.participants = true"
              class="more-link"
              v-text="'+'+ (participants.length - 4)"
            />
          </tooltip>


        </div>
      </div>
    </transition>
    <popup v-if="participants.length" v-model="popups.participants" size="sm">
      <div class="u-text-center">
        <div class="tour-participants__popup-title font-h2">
          {{ $t("common.already") }}
          {{ $tc("participants.count", participants.length) }}
        </div>
        <div class="tour-participants__popup-list">
          <div
            v-for="{
              id,
              user_link,
              first_name,
              avatar_resize,
              city,
            } in participants"
            :key="id"
            class="tour-participants__popup-item font-semibold"
          >
            <div
              :title="first_name"
              class="tour-participants__popup-avatar"
              @click="showDetailPopup(id)"
            >
              <avatar :name="first_name" :photo="avatar_resize" />
            </div>
            <div>{{ first_name }}</div>
            <div v-if="city" class="u-text-secondary">{{ city }}</div>
          </div>
        </div>
      </div>
    </popup>
    <popup v-model="popups.participantDetail" size="sm">
      <div v-if="preloaderDetail" :key="'preloader'">
        <preloader visible />
      </div>
      <div v-else>
        <div class="tour-participants__popup-detail_title font-h2">{{ $t("participants.detailTitle") }}</div>
        <div class="tour-participants__popup-detail_header">
          <div
            class="tour-participants__popup-detail_link-back"
            @click="popups.participantDetail = false; popups.participants = true">Назад</div>
        </div>
        <div class="tour-participants__body">
          <avatar
            :name="participantDetail()?.first_name"
            :photo="participantDetail()?.avatar"
            class="tour-participants__popup-detail_avatar" />
          <div class="tour-participants__popup-detail_name">{{ participantDetail()?.first_name }}</div>
          <div
            class="tour-participants__popup-detail_btn-green"
            @click="isLoggedIn ? bookingSubmit() : showAuthModal()">
            {{ isLoggedIn ? $t("participants.btnBooking") : $t("participants.btnRegister") }}
          </div>
          <div
            v-for="{
              type,
              title,
              text,
              fake_text,
              blur,
            } in participantDetail()?.fields"
            :key="title"
            class="tour-participants__popup-detail_info">
            <div
              v-if="type === 'text'"
              class="tour-participants__popup-detail_info-title">{{ title }}</div>
            <div
              v-if="type === 'text'"
              class="tour-participants__popup-detail_info-text"
              :class="{'tour-participants__popup-detail_info-text-blur': blur}">
              {{ blur ? fake_text : text }}
            </div>
          </div>
        </div>
      </div>
    </popup>
    <button
      v-if="participants.length"
      type="button"
      class="tour-participants__button link u-clickable"
      @click="popups.participants = true"
    >
          <span class="tour-participants__button-text font-semibold">
            {{ $t("participants.view") }}
          </span>
      <svg-inline tag="span" class="u-decor u-stroke-deep-current">
        <icon icon="dropdown" />
      </svg-inline>
    </button>
  </div>
</template>

<script>
import { axiosTour } from "@/services/scopes/axios-tour";
import Tooltip from "@/components/ui-kit/tooltip/index.vue";
import Avatar from "@/components/ui-kit/avatar/index.vue";
import Popup from "@/components/ui-kit/popup/index.vue";
import SvgInline from "@/components/ui-kit/svg-inline/index.vue";
import Icon from "@/components/icons/index.vue";
import Preloader from "@/components/ui-kit/preloader/index.vue";
import {mapState} from "vuex";
import {ytEvents} from "@/plugins/analytics";

export default {
  components: {
    Tooltip,
    Avatar,
    Popup,
    SvgInline,
    Icon,
    Preloader,
  },
  props: {
    tourId: {
      type: [String, Number],
      required: true,
    },
    offerId: {
      type: [String, Number],
      required: true,
    },
    cropBy: {
      type: Number,
      default: 6,
    },
  },
  data() {
    return {
      currentParticipantId: false,
      participants: [],
      preloaderDetail: true,
      popups: {
        participants: false,
        participantDetail: false,
      },
    };
  },
  computed: {
    ...mapState("user", ["isLoggedIn"]),
    params() {
      return [this.tourId, this.offerId];
    },
    croppedParticipants() {
      return this.participants.slice(0, this.cropBy);
    },
  },
  watch: {
    params: {
      deep: true,
      immediate: true,
      handler([tourId, offerId]) {
        this.getParticipants(tourId, offerId);
      },
    },
  },
  mounted() {
    fakeWindow.addEventListener("auth.close", this.onAuthClose);
    fakeWindow.addEventListener("auth.success", this.onAuthSuccess);
  },
  beforeDestroy() {
    fakeWindow.removeEventListener("auth.close", this.onAuthClose);
    fakeWindow.removeEventListener("auth.success", this.onAuthSuccess);
  },
  methods: {
    async getParticipants(tourId, offerId) {

      if (tourId && offerId) {
        await axiosTour
          .getParticipants(tourId, offerId)
          .then(({ data: { data } }) => {
            this.setParticipants(Object.values(data), tourId, offerId);
          })
          .catch(() => this.setParticipants([], tourId, offerId));
      }
    },
    async setParticipants(participants, tourId, offerId) {
      // handling race conditions
      if (tourId === this.tourId && offerId === this.offerId) {
        this.participants = participants;
      }
    },
    async getParticipantDetail(userId, needUpdate = false) {
      this.preloaderDetail = true;
      this.currentParticipantId = userId;

      let existData = this.participants.find(user => user.id === this.currentParticipantId)?.data;
      let isUpdate = (this.isLoggedIn === true && existData?.needAuthUpdate) || needUpdate;
      if (existData !== undefined && !isUpdate) {
          this.setParticipantDetail(userId, existData);
          return;
      }

      await axiosTour
        .getParticipantDetail(this.tourId, this.offerId, userId)
        .then(({ data: { data } }) => {
          this.setParticipantDetail(userId, data);
        })
        .catch(() => {
          this.popups.participantDetail = false;
          this.popups.participants = true;
          this.setParticipantDetail(userId, []);
        });
    },
    setParticipantDetail(userId, userData) {
      let index = this.participants.findIndex(user => user.id === this.currentParticipantId)

      if (index !== -1 && userData !== [] && userData !== undefined) {
        userData.needAuthUpdate = !this.isLoggedIn;
        this.participants[index].data = userData;
      }

      this.preloaderDetail = false;
    },
    showAuthModal() {
      fakeWindow.showVueAuthModal({ "emit-event": 'participant-detail' });
      this.popups.participantDetail = false;
    },
    onAuthClose({ detail }) {
      if (detail === 'participant-detail') {
        this.popups.participantDetail = true;
      }
    },
    onAuthSuccess({ detail }) {
      if (detail === 'participant-detail') {
        this.popups.participantDetail = true;
        let currentParticipantIndex = this.participants.findIndex(user => user.id === this.currentParticipantId);
        this.participants[currentParticipantIndex].data.needAuthUpdate = true;
        this.getParticipantDetail(this.currentParticipantId, true);
      }
    },
    trackEvent(category, name, label) {
      ytEvents.track(category, name, label);
    },
    participantDetail() {
      return this.participants.find(user => user.id === this.currentParticipantId)?.data;
    },
    bookingSubmit() {
      this.popups.participantDetail = false;
      this.trackEvent("Tour detail", "Click Booking button", 'Participants');
      this.$emit('booking');
    },
    showDetailPopup(userId) {
      if (userId === undefined || userId === '') {
        return;
      }

      this.popups.participantDetail = true;
      this.popups.participants = false;
      this.getParticipantDetail(userId)
    },
  },
};
</script>

<style lang="scss" scoped>
.tour-participants {
  margin-top: 20px;
  height: 100%;

  @include mixin.size-md-only {
    margin-top: 10px;
  }

  &__container {
    @include mixin.size-md-only {
      display: flex;
      align-items: center;
    }

    @include mixin.size-xl-min {
      display: flex;
      align-items: center;
    }
  }

  &__list {
    display: flex;
    align-items: center;
  }

  &__avatar {
    position: relative;
    width: 40px;

    &:not(:first-child) {
      margin-left: -2px;

      &::before {
        @include mixin.full-pos(-4px);
        content: "";
        display: block;
        position: absolute;
        background: var.$white;
        border-radius: 50%;
      }
    }

    &-link {
      display: block;
      cursor: pointer;
    }
  }

  &__button {
    @include mixin.button-reset();
    margin-top: 16px;

    @include mixin.size-md-only {
      margin-top: 0;
      margin-left: 16px;
    }

    @include mixin.size-xl-min {
      margin-top: 0;
      margin-left: 16px;
    }

    &-text {
      color: var.$green-dark;
      transition: var.$transition-default;

      &:hover {
        color: var.$green-hover;
      }
    }

    &.link {
      margin: 0;
      height: 63%;
      display: flex;
      align-items: end;

      .svg-inline {
        transform: rotate(-90deg);
        width: 15px;
        margin-top: 2px;
        color: var.$green-dark;
      }

      .font-semibold {
        font-size: 13px;
        line-height: 17px;
      }
    }
  }

  &__popup {
    &-title {
      @include mixin.size-lg-min {
        font-size: 18px;
        line-height: func.divide(22, 18);
      }
    }

    &-list {
      display: flex;
      flex-wrap: wrap;
      margin: 0 -18px;

      @include mixin.size-lg-min {
        margin: 0 -24px;
      }
    }

    &-item {
      padding: 0 18px;
      width: percentage(func.divide(1, 3));
      margin-top: 24px;

      @include mixin.size-lg-min {
        padding: 0 24px;
      }
    }

    &-avatar {
      display: block;
      width: 80px;
      margin: 0 auto 12px;
      cursor: pointer;
    }

    &-detail {
      &_title {
        margin-bottom: 15px;
      }
      &_header {
        height: 150px;
        background: linear-gradient(108.74deg, #662170 5.74%, #662170 96.66%);
        margin-left: -25px;
        margin-right: -25px;
        padding: 23px 40px;
      }

      &_avatar {
        background: #662170;
        text-align: center;
        border-radius: 50%;
        width: 100px;
        height: 100px;
        border: 2px solid #fff;
        margin-top: -51px;
      }

      &_link-back {
        font-family: Roboto,sans-serif;
        font-weight: 500;
        font-size: 12px;
        line-height: 16px;
        color: #9fa2b4;
        position: relative;
        cursor: pointer;

        &::before {
          content: "";
          display: block;
          width: 7px;
          height: 12px;
          background-image: url("/local/templates/youtravel/static/i/trip/left-arrow.png");
          background-repeat: no-repeat;
          background-position: center;
          position: absolute;
          left: -18px;
          top: 1px;
        }
      }

      &_name {
        font-weight: 600;
        font-size: 24px;
        line-height: 28px;
        margin-top: 33px;
        margin-bottom: 20px;
      }

      &_info {
        margin-bottom: 20px;
        font-size: 16px;
        line-height: 20px;

        &-title {
          font-weight: 600;
          color: #9fa2b4;
          margin-bottom: 5px;
        }

        &-text {
          font-size: 16px;
          color: #252733;

          &-blur {
            filter: blur(4px);
          }
        }

        &-singlerange .tour-participants__popup-detail_info-text {
          font-weight: 500;
          font-size: 14px;
          line-height: 18px;
          color: rgba(0,0,0,.4);
          display: flex;
          flex-direction: row;
          justify-content: space-between;
        }
      }

      &_btn-green {
        background-color: #b0cb1f;
        border: 1px solid #b0cb1f;
        color: #fff;
        font-weight: 500;
        font-size: 15px;
        line-height: 22px;
        padding: 10px 20px;
        border-radius: 2px;
        cursor: pointer;
        text-align: center;
        text-transform: uppercase;
        display: block;
        transition: color .3s,background-color .3s,border-color .3s;
        margin-bottom: 10px;

        &:hover {
          background-color: #fff;
          color: #b0cb1f;
        }
      }
    }
  }

  .more-link {
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #9999A9;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    color: #9999A9;
    position: relative;

    @include mixin.size-sm {
      width: 28px;
      height: 28px;
    }

   &:before {
     top: 4px;
     right: 39px;
     bottom: 4px;
     left: -4px;
     content: "";
     display: block;
     position: absolute;
     background: #fff;
    }
  }
}
</style>
